import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { ConfirmDialog } from '@/components/feedback/ConfirmDialog'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { formatDateTime } from '@/lib/format'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { services } from '@/services'
import type { McpConnection, Permission, UserAccess, UserSession } from '@/types/models'

const text = messages.users

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-text-secondary text-xs font-medium">{title}</h3>
      {children}
    </section>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-text-secondary">{label}</span>
      <span className="tabular text-right">{children}</span>
    </div>
  )
}

/** `null` = kısıt yok, boş dizi = bu kapsamı veren izin yok (backend `firm_scopes`). */
function ScopeValue({ scope }: { scope: string[] | null }) {
  if (scope === null) return <Badge variant="neutral">{text.scopeAll}</Badge>
  if (scope.length === 0) return <span className="text-text-muted">{text.scopeNone}</span>
  return (
    <span className="flex flex-wrap justify-end gap-1">
      {scope.map((name) => (
        <Badge key={name} variant="outline">
          {name}
        </Badge>
      ))}
    </span>
  )
}

function PermissionList({ access, catalog }: { access: UserAccess; catalog: Permission[] }) {
  if (access.user.is_superadmin) return <p className="text-sm">{text.superadminAll}</p>
  if (access.permissions.length === 0)
    return <p className="text-text-muted text-sm">{text.noPermissions}</p>

  const granted = new Set(access.permissions)
  const byGroup = new Map<string, Permission[]>()
  for (const permission of catalog) {
    if (!granted.has(permission.key)) continue
    byGroup.set(permission.group, [...(byGroup.get(permission.group) ?? []), permission])
  }
  return (
    <div className="flex flex-col gap-3">
      {[...byGroup.entries()].map(([group, permissions]) => (
        <div key={group} className="flex flex-col gap-1">
          <span className="text-text-muted text-xs">{group}</span>
          <ul className="flex flex-col gap-0.5 text-sm">
            {permissions.map((permission) => (
              <li key={permission.key}>{permission.label}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : messages.errors.unexpected
}

/**
 * Açık oturum / bağlantı listesi: satır başına "Sonlandır", birden fazlaysa "Tümünü …".
 * Her işlem onay ister; backend denetime yazar (ADMIN-PANEL-PLAN Faz 3).
 */
function RevokeList<T extends { id: string }>({
  title,
  queryKey,
  load,
  revoke,
  renderItem,
  text: copy,
}: {
  title: string
  queryKey: readonly unknown[]
  load: () => Promise<T[]>
  revoke: (id?: string) => Promise<number>
  renderItem: (item: T) => React.ReactNode
  text: {
    empty: string
    revoke: string
    revokeAll: string
    confirmTitle: string
    confirmText: string
    confirmAllTitle: string
    confirmAllText: string
  }
}) {
  const queryClient = useQueryClient()
  // `undefined` = onay kapalı, `null` = tümü, string = tek kayıt.
  const [target, setTarget] = useState<string | null | undefined>(undefined)
  const query = useQuery({ queryKey, queryFn: load })
  const mutation = useMutation({
    mutationFn: (id: string | null) => revoke(id ?? undefined),
    onSuccess: () => {
      setTarget(undefined)
      void queryClient.invalidateQueries({ queryKey })
    },
  })
  const items = query.data ?? []

  return (
    <Section title={title}>
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isLoading ? (
        <Loading rows={2} />
      ) : items.length === 0 ? (
        <span className="text-text-muted text-sm">{copy.empty}</span>
      ) : (
        <>
          <ul className="divide-border border-border divide-y rounded-md border">
            {items.map((item) => (
              <li key={item.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <div className="min-w-0 flex-1 text-sm">{renderItem(item)}</div>
                <Button variant="danger" size="sm" onClick={() => setTarget(item.id)}>
                  {copy.revoke}
                </Button>
              </li>
            ))}
          </ul>
          {items.length > 1 && (
            <Button
              variant="danger"
              size="sm"
              className="self-start"
              onClick={() => setTarget(null)}
            >
              {copy.revokeAll}
            </Button>
          )}
        </>
      )}
      <ConfirmDialog
        open={target !== undefined}
        onOpenChange={(open) => {
          if (!open) {
            setTarget(undefined)
            mutation.reset()
          }
        }}
        title={target === null ? copy.confirmAllTitle : copy.confirmTitle}
        description={target === null ? copy.confirmAllText : copy.confirmText}
        confirmLabel={target === null ? copy.revokeAll : copy.revoke}
        pending={mutation.isPending}
        error={mutation.isError ? errorText(mutation.error) : null}
        onConfirm={() => target !== undefined && mutation.mutate(target)}
      />
    </Section>
  )
}

function SessionItem({ session }: { session: UserSession }) {
  return (
    <>
      <p className="truncate" title={session.user_agent ?? undefined}>
        {session.user_agent ?? text.sessions.unknownDevice}
      </p>
      <p className="text-text-secondary tabular text-xs">
        {[session.ip, `${text.sessions.lastRefresh}: ${formatDateTime(session.created_at)}`]
          .filter(Boolean)
          .join(' · ')}
      </p>
    </>
  )
}

function McpItem({ connection }: { connection: McpConnection }) {
  return (
    <>
      <p className="truncate">{connection.client_name ?? text.mcp.unknownClient}</p>
      <p className="text-text-secondary tabular text-xs">
        {`${text.mcp.lastRefresh}: ${formatDateTime(connection.last_refreshed_at)}`}
      </p>
    </>
  )
}

function UnlockButton({ userId }: { userId: string }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const mutation = useMutation({
    mutationFn: () => services.admin.unlockUser(userId),
    onSuccess: (access) => {
      queryClient.setQueryData(queryKeys.admin.userAccess(userId), access)
      setOpen(false)
    },
  })
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
        {text.unlock}
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) mutation.reset()
        }}
        title={text.unlockConfirmTitle}
        description={text.unlockConfirmText}
        confirmLabel={text.unlock}
        pending={mutation.isPending}
        error={mutation.isError ? errorText(mutation.error) : null}
        onConfirm={() => mutation.mutate()}
      />
    </>
  )
}

function AccessDetail({ access, catalog }: { access: UserAccess; catalog: Permission[] }) {
  const userId = access.user.id
  return (
    <div className="flex flex-col gap-6">
      <Section title={text.detailSections.status}>
        <Field label={text.isActive}>
          <Badge variant={access.user.is_active ? 'success' : 'outline'}>
            {access.user.is_active ? messages.common.active : messages.common.inactive}
          </Badge>
        </Field>
        <Field label={text.lastLogin}>
          {access.user.last_login_at
            ? formatDateTime(access.user.last_login_at)
            : text.neverLoggedIn}
        </Field>
        <Field label={text.lockedUntil}>
          {access.is_locked ? (
            <Badge variant="danger">{formatDateTime(access.locked_until)}</Badge>
          ) : (
            text.notLocked
          )}
        </Field>
        <Field label={text.failedLogins}>{access.failed_login_count}</Field>
        <div className="flex flex-wrap gap-2">
          {(access.is_locked || access.failed_login_count > 0) && <UnlockButton userId={userId} />}
          <Button
            variant="secondary"
            size="sm"
            nativeButton={false}
            render={<Link to={`/yonetim/denetim?actor_id=${userId}`} />}
          >
            {text.auditLink}
          </Button>
        </div>
        {access.must_change_password && (
          <Badge variant="outline" className="self-start">
            {text.mustChangePassword}
          </Badge>
        )}
      </Section>

      <Section title={text.detailSections.groups}>
        {access.user.is_superadmin ? (
          <Badge className="self-start">superadmin</Badge>
        ) : access.groups.length === 0 ? (
          <span className="text-text-muted text-sm">{text.noGroups}</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {access.groups.map((group) => (
              <Badge key={group.id} variant="neutral">
                {group.name}
              </Badge>
            ))}
          </div>
        )}
      </Section>

      <Section title={text.detailSections.scope}>
        {(['hr', 'finans', 'kantar'] as const).map((domain) => (
          <Field key={domain} label={text.firmScopes[domain]}>
            <ScopeValue scope={access.firm_scopes[domain]} />
          </Field>
        ))}
      </Section>

      <RevokeList<UserSession>
        title={text.sessions.title}
        queryKey={queryKeys.admin.userSessions(userId)}
        load={() => services.admin.listUserSessions(userId)}
        revoke={(id) => services.admin.revokeUserSessions(userId, id)}
        renderItem={(session) => <SessionItem session={session} />}
        text={text.sessions}
      />

      <RevokeList<McpConnection>
        title={text.mcp.title}
        queryKey={queryKeys.admin.userMcp(userId)}
        load={() => services.admin.listUserMcpConnections(userId)}
        revoke={(id) => services.admin.revokeUserMcpConnections(userId, id)}
        renderItem={(connection) => <McpItem connection={connection} />}
        text={text.mcp}
      />

      <Section
        title={`${text.detailSections.permissions} · ${text.permissionCount(access.permissions.length)}`}
      >
        <PermissionList access={access} catalog={catalog} />
      </Section>
    </div>
  )
}

/** Kullanıcılar listesinden açılan sağ panel (docs/ADMIN-PANEL-PLAN.md Faz 2). */
export function UserDetailPanel({
  userId,
  onClose,
}: {
  userId: string | null
  onClose: () => void
}) {
  const accessQuery = useQuery({
    queryKey: queryKeys.admin.userAccess(userId ?? ''),
    queryFn: () => services.admin.getUserAccess(userId!),
    enabled: userId !== null,
  })
  const catalogQuery = useQuery({
    queryKey: queryKeys.admin.permissions(),
    queryFn: () => services.admin.listPermissions(),
    enabled: userId !== null,
  })
  const access = accessQuery.data

  return (
    <Sheet open={userId !== null} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right">
        <SheetHeader>
          <SheetTitle>{access?.user.full_name ?? text.detailTitle}</SheetTitle>
          <SheetDescription>{access?.user.email ?? ''}</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {accessQuery.isError ? (
            <ErrorState error={accessQuery.error} onRetry={() => void accessQuery.refetch()} />
          ) : catalogQuery.isError ? (
            <ErrorState error={catalogQuery.error} onRetry={() => void catalogQuery.refetch()} />
          ) : access && catalogQuery.data ? (
            <AccessDetail access={access} catalog={catalogQuery.data} />
          ) : (
            <Loading rows={6} />
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  )
}
